import { Inbox } from "lucide-react";
import type { ReactNode } from "react";

import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { OtcOrder } from "../../types/cryptoSwaps.types";
import { shortenAddress } from "../../utils/cryptoSwapAddress";
import { MonoValue } from "../shared/CryptoSwapBits";
import { NetworkName } from "../shared/NetworkIcon";
import { OrderAmounts } from "../shared/OrderAmounts";
import { CryptoSwapStatusPill } from "./CryptoSwapStatusPill";

export interface CryptoSwapOrdersTableProps {
  rows: OtcOrder[];
  isLoading: boolean;
  isFiltered: boolean;
  onOpen: (order: OtcOrder) => void;
  footer?: ReactNode;
}

/** The history on `lg` and up. The price lives in the drawer, next to a label saying which way it reads. */
export function CryptoSwapOrdersTable({
  rows,
  isLoading,
  isFiltered,
  onOpen,
  footer,
}: CryptoSwapOrdersTableProps) {
  const { t, activeLanguage } = useTranslations("cryptoSwaps");

  const columns: ColocaDataTableColumn<OtcOrder>[] = [
    {
      id: "date",
      header: t("table_col_date"),
      headerClassName: "w-44",
      cell: (order) => (
        <MonoValue className="text-xs text-muted-foreground">
          {formatDateTime(order.created_at, { locale: activeLanguage })}
        </MonoValue>
      ),
    },
    {
      id: "operation",
      header: t("table_col_operation"),
      cell: (order) => <OrderAmounts order={order} />,
    },
    {
      id: "destination",
      header: t("table_col_destination"),
      headerClassName: "w-56",
      cell: (order) => (
        <span className="flex flex-col">
          <MonoValue className="text-[13px]">{shortenAddress(order.destination.address) || "—"}</MonoValue>
          <NetworkName
            network={order.destination.network}
            size={14}
            className="text-xs text-muted-foreground"
          />
        </span>
      ),
    },
    {
      id: "status",
      header: t("table_col_status"),
      headerClassName: "w-40 text-right",
      cellClassName: "text-right",
      cell: (order) => <CryptoSwapStatusPill status={order.status} />,
    },
  ];

  return (
    <ColocaDataTable
      columns={columns}
      rows={rows}
      rowKey={(order) => order.id}
      onRowClick={onOpen}
      loading={isLoading}
      footer={footer}
      empty={
        <ColocaEmpty
          surface="card"
          icon={Inbox}
          title={isFiltered ? t("table_no_results_title") : t("table_empty_title")}
          description={isFiltered ? t("table_no_results_description") : t("table_empty_description")}
        />
      }
    />
  );
}
